/**
 * Signal Validation Logic
 * 
 * Validates configured signals against their schema definitions.
 */

import { SIGNAL_REGISTRY } from "./registry";
import { ConfiguredSignal, ValidationResult, ParamSchema } from "./types";

/**
 * Validate a single configured signal
 */
export function validateSignal(signal: ConfiguredSignal): ValidationResult {
  const schema = SIGNAL_REGISTRY[signal.type];
  
  if (!schema) {
    return { valid: false, error: `Unknown signal type: "${signal.type}"` };
  }

  // Check for no params required (e.g., on_exit)
  if (Object.keys(schema.params).length === 0) {
    return { valid: true };
  }

  // Check required params
  const missingParams: string[] = [];
  for (const [key, paramSchema] of Object.entries(schema.params)) {
    if (paramSchema.required) {
      const value = signal.params[key];
      if (value === undefined || value === null || value === "") {
        missingParams.push(paramSchema.label);
        continue;
      }
      if (paramSchema.type === "frequency") {
        const freq = value as { every?: unknown; unit?: unknown };
        const everyOk =
          typeof freq.every === "number" && Number.isInteger(freq.every) && freq.every > 0;
        const unitOk =
          typeof freq.unit === "string" &&
          (freq.unit === "seconds" || freq.unit === "minutes" || freq.unit === "hours");
        if (!everyOk || !unitOk) {
          missingParams.push(paramSchema.label);
        }
      }
    }
  }

  if (missingParams.length > 0) {
    return {
      valid: false,
      error: `Missing required: ${missingParams.join(", ")}`,
      missingParams,
    };
  }

  // Run parameter-level validation if exists
  if (schema.validateParam) {
    for (const [key, value] of Object.entries(signal.params)) {
      if (value !== undefined && value !== null && value !== "") {
        const result = schema.validateParam(key, value);
        if (result !== true) {
          return { valid: false, error: result };
        }
      }
    }
  }

  // Run custom validator if exists
  if (schema.validate) {
    const result = schema.validate(signal.params);
    if (result !== true) {
      return { valid: false, error: result };
    }
  }

  return { valid: true };
}

/**
 * Check if a signal has all required params filled
 */
export function isSignalComplete(signal: ConfiguredSignal): boolean {
  return validateSignal(signal).valid;
}

/**
 * Get display summary of a configured signal
 * e.g., "YTD Return: >15%, <-10%"
 */
export function getSignalSummary(signal: ConfiguredSignal): string {
  // Don't show summary for empty or unknown signal types
  if (!signal.type) return "";

  const schema = SIGNAL_REGISTRY[signal.type];
  if (!schema) return "";  // Unknown type - don't show anything

  const parts: string[] = [];

  for (const [key, paramSchema] of Object.entries(schema.params)) {
    const value = signal.params[key];
    if (value === undefined || value === null || value === "") continue;

    const formattedValue = formatParamValue(value, paramSchema);
    if (formattedValue) {
      parts.push(formattedValue);
    }
  }

  return parts.length > 0 ? parts.join(", ") : "";
}

/**
 * Format a parameter value for display
 */
function formatParamValue(value: unknown, schema: ParamSchema): string {
  if (value === undefined || value === null || value === "") return "";

  switch (schema.type) {
    case "percent":
      // Convert from decimal to percentage for display (0.03 -> 3%)
      if (typeof value === "number") {
        return `${(value * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
      }
      return `${value}%`;
    case "dollar":
      return `$${value}`;
    case "select":
      const option = schema.options?.find(o => o.value === value);
      return option?.label || String(value);
    case "frequency": {
      const freq = value as { every?: number; unit?: string };
      if (freq && typeof freq.every === "number" && freq.unit) {
        return `every ${freq.every} ${freq.unit}`;
      }
      return "";
    }
    default:
      return String(value);
  }
}

/**
 * Create a new empty configured signal
 */
export function createEmptySignal(type: string): ConfiguredSignal {
  return {
    id: generateId(),
    type,
    params: {},
  };
}

/**
 * Generate unique ID
 */
export function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

